import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert } from '@mui/material';
import axios from 'axios';
import url from '../url.js';
import createActivityImg from '../assets/undraw_creative_thinking_re_9k71.svg';

const EditProfileModal = ({ open, onClose }) => {
    
    const [userData, setUserData] = useState({
        name: '',
        email: '',
    });
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    // 取出單個 Cookie

    useEffect(() => {
        const fetchUserData = async () => {
            const userId = localStorage.getItem('userId'); // 取得當前使用者 ID
            if (!userId) {
                setErrorMessage('無法取得使用者資訊，請重新登入');
                return;
            }

            try {
                setDataLoading(true);
                setErrorMessage('');
                const response = await axios.get(`${url.backendHost}api/users/${userId}`);
                
                if (response.data) {
                    setUserData({
                        name: response.data.name || '',
                        // email: response.data.email || '',
                    });
                } else {
                    setErrorMessage('無法載入使用者資料');
                }
            } catch (error) {
                console.error('Failed to fetch user data:', error);
                if (error.response?.status === 404) {
                    setErrorMessage('找不到使用者資料，請確認您已正確登入');
                } else if (error.response?.status === 401) {
                    setErrorMessage('登入已過期，請重新登入');
                } else {
                    setErrorMessage('載入使用者資料時發生錯誤，請稍後再試');
                }
            } finally {
                setDataLoading(false);
            }
        };

        if (open) {
            fetchUserData();
            // 重置表單狀態
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setErrorMessage('');
        }
    }, [open]);

    const handleSave = async () => {
        if (!currentPassword) {
            setErrorMessage('請輸入當前密碼進行驗證');
            return;
        }
        if (newPassword && newPassword !== confirmPassword) {
            setErrorMessage('新密碼與確認密碼不一致');
            return;
        }

        const userId = localStorage.getItem('userId'); 
        if (!userId) {
            setErrorMessage('無法取得使用者資訊，請重新登入');
            return;
        }

        try {
            setLoading(true);
            setErrorMessage('');

            await axios.put(`${url.backendHost}api/users/${userId}`, {
                name: userData.name,
                // email: userData.email,
                currentPassword, 
                ...(newPassword && { password: newPassword }), 
            });

            alert('個人資料已更新成功');
            onClose(); 
        } catch (error) {
            console.error('Failed to update profile:', error);
            if (error.response?.status === 401) {
                setErrorMessage('當前密碼不正確，請重新輸入');
            } else if (error.response?.status === 404) {
                setErrorMessage('找不到使用者資料');
            } else {
                setErrorMessage(error.response?.data?.message || '更新失敗，請稍後再試');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading && !dataLoading) {
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <div>
                <img className='modal-image' src={createActivityImg} alt="Edit Profile" />
            </div>
            <DialogTitle sx={{ color: 'black', textDecoration: 'none' }}>編輯個人資料</DialogTitle>
            <DialogContent>
                {dataLoading ? (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        py: 4 
                    }}>
                        <CircularProgress sx={{ mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                            載入使用者資料中...
                        </Typography>
                    </Box>
                ) : errorMessage && !userData.name ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errorMessage}
                        <Box sx={{ mt: 1 }}>
                            <Button 
                                size="small" 
                                onClick={() => window.location.reload()}
                                variant="outlined"
                                color="error"
                            >
                                重新載入
                            </Button>
                        </Box>
                    </Alert>
                ) : (
                    <>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="name"
                            label="姓名"
                            type="text"
                            name="name"
                            value={userData.name}
                            fullWidth
                            variant="standard"
                            disabled={loading}
                            onChange={(e) => setUserData({ ...userData, name: e.target.value })}
                            helperText={!userData.name ? "請輸入您的姓名" : ""}
                        />
                        <TextField
                            margin="dense"
                            id="currentPassword"
                            label="當前密碼"
                            type="password"
                            name="currentPassword"
                            value={currentPassword}
                            fullWidth
                            required
                            variant="standard"
                            disabled={loading}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            helperText="為了安全考量，修改資料需要驗證當前密碼"
                        />
                        <TextField
                            margin="dense"
                            id="newPassword"
                            label="新密碼（選填）"
                            type="password"
                            name="newPassword"
                            value={newPassword}
                            fullWidth
                            variant="standard"
                            disabled={loading}
                            onChange={(e) => setNewPassword(e.target.value)}
                            helperText="如果不需要修改密碼，請留空"
                        />
                        <TextField
                            margin="dense"
                            id="confirmPassword"
                            label="確認新密碼"
                            type="password"
                            name="confirmPassword"
                            value={confirmPassword}
                            fullWidth
                            variant="standard"
                            disabled={loading || !newPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />

                        {errorMessage && userData.name && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {errorMessage}
                            </Alert>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading || dataLoading}>
                    取消
                </Button>
                <Button 
                    onClick={handleSave} 
                    disabled={loading || dataLoading || !userData.name}
                    variant="contained"
                >
                    {loading ? (
                        <>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            儲存中...
                        </>
                    ) : (
                        '儲存'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditProfileModal;
